import { createReducer } from '__common/utils/helpers';
import { insertPanelIntoRtree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { removePanelFromRtree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { resetRTree } from '__editor/panelsEditor/components/panels/panelsCollisions';
import { PanelsActionTypes } from 'actionsConstants';
import {
  getPanelCollisionBounds,
} from '__editor/panelsEditor/components/panels/panels';
import { checkObstructionZoneForPanelsInPanelEditor } from '../obstructions/obstructionsHelper';
import { removePanelsFromState, setExposureForSibligns } from './utils/panelsManagment';
import { checkPanelsExposure } from './utils/exposureRecheck';
import hash from 'object-hash';
import { Action } from '__common/store/action';
import { ADD_PANELS_ACTION, LOAD_PANELS_ACTION, UPDATE_PANELS_ACTION, RECHECK_PANELS_EXPO_ACTION, CHANGE_DESIRED_TABLE_LENGTH_ACTION, CHANGE_DESIRED_TABLE_WIDTH_ACTION, ADD_HASHMAP_ACTION } from './panelsActions';
import store from '__common/store';
import { isSingleColumnRestrictedProduct } from '__common/constants/products';
import { greaterThanEqualToProjectVersion } from '__common/utils/versionCompare/versionCompare';
import { VERSION_MAP } from '__common/utils/versionCompare/version_info';
import { applySingleColumnPendingRemovalState, SingleColumnSpacingPx } from './utils/singleColumnPendingRemoval';

function applyManualExposureToPanel(panel: panelInState, kind: 'E' | 'NE'): panelInState {
  if (panel.manualExposure === kind) {
    const cleared = { ...panel };
    if (cleared.manualExposureOrigExposed !== undefined) {
      cleared.exposed = !!cleared.manualExposureOrigExposed;
    }
    if (cleared.manualExposureOrigExposedNeighbour !== undefined) {
      cleared.exposedNeighbour = !!cleared.manualExposureOrigExposedNeighbour;
    }
    delete cleared.manualExposure;
    delete cleared.manualExposureOrigExposed;
    delete cleared.manualExposureOrigExposedNeighbour;
    return cleared;
  }
  const origExposed = panel.manualExposureOrigExposed !== undefined
    ? panel.manualExposureOrigExposed
    : panel.exposed;
  const origExposedNeighbour = panel.manualExposureOrigExposedNeighbour !== undefined
    ? panel.manualExposureOrigExposedNeighbour
    : panel.exposedNeighbour;
  return {
    ...panel,
    manualExposure: kind,
    exposed: kind === 'E',
    exposedNeighbour: kind === 'E' ? !!panel.exposedNeighbour : false,
    manualExposureOrigExposed: origExposed,
    manualExposureOrigExposedNeighbour: origExposedNeighbour,
  };
}

const shouldApplySingleColumnPendingRemoval = () => {
  const { projectConfiguration: { productId, projectVersion } } = store.getState();

  return isSingleColumnRestrictedProduct(productId) &&
    greaterThanEqualToProjectVersion(projectVersion, VERSION_MAP['single_column_restriction']);
};

const getSingleColumnSpacingPx = (spacingPx?: SingleColumnSpacingPx): SingleColumnSpacingPx | undefined => {
  if (spacingPx) {
    return spacingPx;
  }

  const { settings: { rowSpacing, columnSpacing }, background: { metersPerPixel } } = store.getState();

  if (!metersPerPixel && metersPerPixel !== 0) {
    return undefined;
  }

  return {
    rowSpacingPx: rowSpacing / metersPerPixel,
    columnSpacingPx: columnSpacing / metersPerPixel,
  };
};

const applySingleColumnPendingRemovalIfNeeded = (
  panels: panelInState[],
  spacingPx?: SingleColumnSpacingPx,
) => {
  if (!shouldApplySingleColumnPendingRemoval()) {
    return panels;
  }

  return applySingleColumnPendingRemovalState(
    panels,
    getSingleColumnSpacingPx(spacingPx),
  );
};

export const panelsState: panelsState = {
  panels: [],
  centerOffset: { x: 0, y: 0 },
  creatingBlocked: false,
  enabledRoofZones: true,
  exposureRecalculation: false,
  exposureRecaluclationHash: null,
  desiredTableLength: null,
  desiredTableWidth: null,
  panelsToBeMoved: [],
  panelsArrangementToBeMoved: { panelsToBeMovedGrid: [], panelsToggleAttachmentGrid: [], panelConfig: null },
  bays: {
    panelBayMapping: new Map(),
    visitedBays: []
  }
};

export default {
  panels: createReducer(panelsState, {
    // Adds new panels to the state
    [PanelsActionTypes.ADD_PANELS](state: panelsState, action: Action<ADD_PANELS_ACTION>): panelsState {
      return {
        ...state,
        panels: action.payload.panels,
      };
    },
    // Updates the mapping between panels and bays
    [PanelsActionTypes.ADD_PANEL_BAY_MAPPING](state: panelsState, action: Action<ADD_HASHMAP_ACTION>): panelsState {
      return {
        ...state,
        bays: {
          ...state.bays,
          panelBayMapping: action.payload.panelBayMapping
        },
      };
    },
    // Starts the process of recalculating panel exposure
    [PanelsActionTypes.START_EXPOSURE_RECALCULATION](state): panelsState {
      return { ...state, exposureRecalculation: true };
    },
    [PanelsActionTypes.FINISH_EXPOSURE_RECALCULATION](state): panelsState {
      return { ...state, exposureRecalculation: false };
    },
    // Rechecks exposure for specific panels and updates siblings
    [PanelsActionTypes.RECHECK_PANELS_EXPO](state: panelsState, action: Action<RECHECK_PANELS_EXPO_ACTION>): panelsState {
      const {
        panelsToRecheck,
        productId,
        roofId,
      } = action.payload;
      let panels = checkPanelsExposure(state.panels, panelsToRecheck, roofId);
      panels = setExposureForSibligns(panels, productId);
      const exposureRecaluclationHash = hash(panels);
      return { ...state, panels, exposureRecalculation: false, exposureRecaluclationHash };
    },
    // Checks if panels are within obstruction zones
    [PanelsActionTypes.RECHECK_PANELS_OBSTRUCTION_ZONE](state: panelsState) {
      const panels = checkObstructionZoneForPanelsInPanelEditor(state.panels);
      return { ...state, panels };
    },
    // Removes panels from state and cleans up associated bay mappings
    [PanelsActionTypes.REMOVE_PANELS](state: panelsState, action: action & { payload: { panelsIds: number[] } }): panelsState {
      const { panelsIds } = action.payload;
      let { panelBayMapping, visitedBays } = state.bays;
      const panelsToRemove = state.panels.filter(panel => panelsIds.includes(panel.id));

      if (!Array.isArray(visitedBays)) {
        visitedBays = [];
      }

      // Clear collision data synchronously so users can immediately add modules
      // back into the same location without waiting for the PIXI redraw cleanup.
      panelsToRemove.forEach((panel) => {
        if (panel.rTreeBounds) {
          removePanelFromRtree(panel.rTreeBounds);
        }
      });

      // Cleanup bay mappings and visited bays for removed panels
      if (panelBayMapping instanceof Map && panelBayMapping.size) {
        panelBayMapping = new Map(panelBayMapping);
        const deletedBaysSet = new Set<number>();
        panelsIds.forEach((id: number) => {
          const bays = panelBayMapping.get(id);
          if (bays) {
            bays.forEach(bay => deletedBaysSet.add(bay.id));
            panelBayMapping.delete(id);
          }
        });
        // Filtering visitedBays no longer causes assignment errors after updating panelsState.d.ts to number[]
        visitedBays = visitedBays.filter(id => !deletedBaysSet.has(id));
      }

      const panels = removePanelsFromState(state.panels, panelsIds);

      // Re-sync RTree with the final list of panels after filtering/pruning
      // (some products like RMDT might remove siblings automatically)
      const { settings: { rowSpacing, columnSpacing }, background: { metersPerPixel } } = store.getState();
      resetRTree();
      panels.forEach((panel) => {
        const bounds = getPanelCollisionBounds(panel, rowSpacing, columnSpacing, metersPerPixel);
        panel.rTreeBounds = bounds;
        insertPanelIntoRtree(bounds);
      });

      return {
        ...state,
        panels,
        exposureRecaluclationHash: null,
        bays: {
          ...state.bays,
          panelBayMapping,
          visitedBays
        }
      };
    },
    [PanelsActionTypes.BLOCK_CREATING_PANEL](state: panelsState) {
      return { ...state, creatingBlocked: true };
    },
    [PanelsActionTypes.UNBLOCK_CREATING_PANEL](state: panelsState) {
      return { ...state, creatingBlocked: false };
    },
    // Resets the entire panels state to initial values
    [PanelsActionTypes.RESET_PANELS_STATE](state: panelsState) {
      return { ...state, panels: [], creatingBlocked: false, exposureRecaluclationHash: null, panelsToBeMoved: [], panelsArrangementToBeMoved: { panelsToBeMovedGrid: [], panelsToggleAttachmentGrid: [], panelConfig: null }, bays: { panelBayMapping: new Map(), visitedBays: [] } };
    },
    // Loads panels from a previous state (undo/redo or history)
    [PanelsActionTypes.LOAD_PANELS_FROM_HISTORY](state: panelsState, action: action & { payload: { panels: panelInState[] } }): panelsState {
      const {
        settings: { rowSpacing, columnSpacing },
        background: { metersPerPixel },
      } = store.getState();

      resetRTree();

      const panels = (action.payload.panels || []).map((panel) => {
        const rTreeBounds = getPanelCollisionBounds(panel, rowSpacing, columnSpacing, metersPerPixel);
        insertPanelIntoRtree(rTreeBounds);

        return {
          ...panel,
          rTreeBounds,
        };
      });

      return {
        ...state,
        panels,
        exposureRecaluclationHash: null,
      };
    },
    // Standard panel loading with collision initialization.
    // Snap/cursor trees are hydrated from the rendered PIXI panels so they can be
    // cleaned up from the same object graph later.
    [PanelsActionTypes.LOAD_PANELS](state: panelsState, action: Action<LOAD_PANELS_ACTION>): panelsState {
      const { rowSpacing, columnSpacing, metersPerPixel, exposureRecaluclationHash } = action.payload;
      const panelsToLoad = action.payload.panels
     

      const panels = panelsToLoad.map((panel) => {
        const updatedPanel = { ...panel };

        if (action.payload.shouldCenter) {
          updatedPanel.x += state.centerOffset.x;
          updatedPanel.y += state.centerOffset.y;
        }

        const bounds = getPanelCollisionBounds(updatedPanel, rowSpacing, columnSpacing, metersPerPixel);

        updatedPanel.rTreeBounds = bounds;
        insertPanelIntoRtree(bounds);

        return updatedPanel;
      });

      return {
        ...state,
        panels,
        centerOffset: { x: 0, y: 0 },
        exposureRecaluclationHash,
      };
    },
    // Re-calculates and inserts collision bounds for all current panels
    [PanelsActionTypes.RESTORE_PANELS_COLLISIONS_BOUNDS](state: panelsState, action) {
      const { rowSpacing, columnSpacing, metersPerPixel } = action.payload;

      const panels = state.panels.map((panel) => {
        const rTreeBounds = getPanelCollisionBounds(panel, rowSpacing, columnSpacing, metersPerPixel);
        insertPanelIntoRtree(rTreeBounds);
        return { ...panel, rTreeBounds };
      });

      return { ...state, panels };
    },
    // Sets whether roof zone autofill is enabled
    [PanelsActionTypes.SET_ROOF_ZONES_AUTOFILL](state: panelsState, action: { payload: { status: boolean } }) {
      return { ...state, enabledRoofZones: action.payload.status };
    },
    // Changes the roof zone of a specific panel
    [PanelsActionTypes.CHANGE_SINGLE_PANEL_ROOF_ZONE](state: panelsState, action: { payload: { panelId: number, newRoofZone: roofZoneNumber } }) {
      const { panels } = state;
      const { panelId, newRoofZone } = action.payload;

      const updatedPanels = panels.map((panel) => {
        if (panel.id === panelId) {
          return { ...panel, roofZone: newRoofZone };
        }
        return panel;
      });

      return { ...state, panels: updatedPanels };
    },
    // Sets the global center offset for panels
    [PanelsActionTypes.SET_CENTER_OFFSET](state: panelsState, action: { payload: { offset: { x: number, y: number } } }) {
      const { offset } = action.payload;

      return { ...state, centerOffset: offset };
    },
    // Updates existing panels with new data, optionally targeting specific properties
    [PanelsActionTypes.UPDATE_PANELS](state: panelsState, action: Action<UPDATE_PANELS_ACTION>): panelsState {
      const { payload: { panels, type } } = action;
      const panelsMap = new Map(panels.map(p => [p.id, p]));
      const updatedPanels = state.panels.map((oldPanel) => {
        const foundPanel = panelsMap.get(oldPanel.id);
        if (foundPanel) {
          return {
            ...oldPanel,
            ...(type?.type ? { [type?.type]: foundPanel[type?.type] } : foundPanel),
          };
        }
        return oldPanel;
      });
      const updatedPanelsToBeMoved = state.panelsToBeMoved.map((oldPanel) => {
        const foundPanel = panelsMap.get(oldPanel.id);
        if (foundPanel) {
          return {
            ...oldPanel,
            ...(type?.type ? { [type?.type]: foundPanel[type?.type] } : foundPanel),
          };
        }
        return oldPanel;
      });
      return { ...state, panels: updatedPanels, panelsToBeMoved: updatedPanelsToBeMoved };
    },
    // Sets desired table dimensions for GFT/ULA
    [PanelsActionTypes.CHANGE_DESIRED_TABLE_LENGTH](state: panelsState, action: Action<CHANGE_DESIRED_TABLE_LENGTH_ACTION>): panelsState {
      return { ...state, desiredTableLength: action.payload.length };
    },
    [PanelsActionTypes.CHANGE_DESIRED_TABLE_WIDTH](state: panelsState, action: Action<CHANGE_DESIRED_TABLE_WIDTH_ACTION>): panelsState {
      return { ...state, desiredTableWidth: action.payload.width };
    },
    [PanelsActionTypes.RESET_TABLE_LENGTH_AND_WIDTH_SIZE](state: panelsState): panelsState {
      return { ...state, desiredTableLength: null, desiredTableWidth: null };
    },
    // Manages panel selection for movement operations
    [PanelsActionTypes.SET_PANELS_ARRAY_FOR_MOVEMENT](state: panelsState, action): panelsState {
      return { ...state, panelsToBeMoved: action.payload.panels };
    },
    [PanelsActionTypes.SET_PANELS_ARRAY_GRID_FOR_MOVEMENT](state: panelsState, action): panelsState {
      const { grid, toggleAttachmentsGrid, panelConfig } = action.payload;
      return { ...state, panelsArrangementToBeMoved: { panelsToBeMovedGrid: grid, panelsToggleAttachmentGrid: toggleAttachmentsGrid, panelConfig: panelConfig } };
    },
    [PanelsActionTypes.RESET_PANELS_ARRAY_FOR_MOVEMENT](state: panelsState, action): panelsState {
      return { ...state, panelsToBeMoved: [], panelsArrangementToBeMoved: { panelsToBeMovedGrid: [], panelsToggleAttachmentGrid: [], panelConfig: null } };
    },
    // Toggles the overall attachment state of a panel
    [PanelsActionTypes.TOGGLE_PANEL_ATTACHED](state: panelsState, action): panelsState {
      const panelId = action.payload.panelId;
      const panels = state.panels.map(panel => {
        if (panel.id === panelId) {
          return { ...panel, attached: !panel.attached };
        }
        return panel;
      });
      return { ...state, panels };
    },
    [PanelsActionTypes.SET_MANUAL_EXPOSURE](state: panelsState, action): panelsState {
      const { panelId, kind } = action.payload;
      const panels = state.panels.map(panel => {
        if (panel.id !== panelId) return panel;
        return applyManualExposureToPanel(panel, kind);
      });
      return { ...state, panels };
    },
    [PanelsActionTypes.SET_MANUAL_EXPOSURE_BULK](state: panelsState, action): panelsState {
      const { panelIds, kind } = action.payload;
      const idSet = new Set<number>(panelIds);
      const panels = state.panels.map(panel => {
        if (!idSet.has(panel.id)) return panel;
        return applyManualExposureToPanel(panel, kind);
      });
      return { ...state, panels };
    },
    [PanelsActionTypes.CLEAR_ALL_MANUAL_EXPOSURE](state: panelsState): panelsState {
      const panels = state.panels.map(panel => {
        if (
          panel.manualExposure === undefined &&
          panel.manualExposureOrigExposed === undefined &&
          panel.manualExposureOrigExposedNeighbour === undefined
        ) {
          return panel;
        }
        const cleared = { ...panel };
        if (cleared.manualExposureOrigExposed !== undefined) {
          cleared.exposed = !!cleared.manualExposureOrigExposed;
        }
        if (cleared.manualExposureOrigExposedNeighbour !== undefined) {
          cleared.exposedNeighbour = !!cleared.manualExposureOrigExposedNeighbour;
        }
        delete cleared.manualExposure;
        delete cleared.manualExposureOrigExposed;
        delete cleared.manualExposureOrigExposedNeighbour;
        return cleared;
      });
      return { ...state, panels };
    },
    // Toggles attachment for a specific bay within a panel
    [PanelsActionTypes.TOGGLE_BAY_ATTACHED](state: panelsState, action): panelsState {
      const bayId = action.payload.bayId;
      const panelId = action.payload.panelId;
      const { panelBayMapping } = state.bays;

      if (!(panelBayMapping instanceof Map)) return state;

      // shallow copy of the Map 
      const mapping = new Map(panelBayMapping);

      // Get the specific array of bays for this panel
      const bays = mapping.get(panelId);

      if (bays) {
        const updatedBays = bays.map(bay => {
          if (bay.id === bayId) {
            return { ...bay, attached: (bay.attached ? 0 : 1) as 0 | 1 };
          }
          return bay;
        });
        mapping.set(panelId, updatedBays);
      }

      return { ...state, bays: { ...state.bays, panelBayMapping: mapping } };
    },
    // Updates the list of visited bays
    [PanelsActionTypes.ADD_VISITED_BAYS](state: panelsState, action): panelsState {
      const bays = action.payload.bays;
      return { ...state, bays: { ...state.bays, visitedBays: bays } };
    },
  }),
};
